// Analytic hidden-line removal for the SVG isometric view. Every front-facing face is an occluder;
// each edge is split where it crosses an occluder's outline or passes through its plane, and each
// piece is hidden when some occluder covers it on screen and lies nearer to the camera.
import { Point } from "@common/components/Rendering";
import { Point3D } from "./isometric";

/** A point projected to the screen, with its depth along the view direction (larger = nearer). */
interface ViewVertex {
  point: Point;
  depth: number;
}

interface ResolvedEdgePiece {
  from: Point;
  to: Point;
  hidden: boolean;
  title?: string;
}

interface Occluder {
  loops: Point[][];
  // depth = a * x + b * y + c anywhere on the (planar) face
  a: number;
  b: number;
  c: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

interface PendingEdge {
  from: ViewVertex;
  to: ViewVertex;
  title?: string;
}

const lerpPoint = (from: Point, to: Point, t: number): Point => [
  from[0] + (to[0] - from[0]) * t,
  from[1] + (to[1] - from[1]) * t,
];

// Even-odd, so a loop inside another (a hole) cuts it out.
const insideLoops = ([x, y]: Point, loops: Point[][]): boolean => {
  let inside = false;
  for (const loop of loops) {
    for (let i = 0, j = loop.length - 1; i < loop.length; j = i++) {
      const [xi, yi] = loop[i];
      const [xj, yj] = loop[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
    }
  }
  return inside;
};

// Parameter along from->to where it crosses segment p->q, if it does.
const crossingParameter = (from: Point, to: Point, p: Point, q: Point): number | null => {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const ex = q[0] - p[0];
  const ey = q[1] - p[1];
  const denominator = dx * ey - dy * ex;
  if (Math.abs(denominator) < 1e-12) return null;
  const fx = p[0] - from[0];
  const fy = p[1] - from[1];
  const t = (fx * ey - fy * ex) / denominator;
  const u = (fx * dy - fy * dx) / denominator;
  return t > 0 && t < 1 && u >= 0 && u <= 1 ? t : null;
};

export class HiddenLineScene {
  private occluders: Occluder[] = [];
  private edges: PendingEdge[] = [];
  private extent = 0;

  constructor(private readonly toView: (p: Point3D) => ViewVertex) {}

  /** Registers a planar front-facing face; extra loops are holes. */
  addOccluder(loops: Point3D[][]): void {
    const viewLoops = loops.filter((loop) => loop.length >= 3).map((loop) => loop.map(this.toView));
    if (viewLoops.length === 0) return;

    // Newell's method over (x, y, depth) gives the face plane; nz ~ 0 means seen edge-on.
    let nx = 0;
    let ny = 0;
    let nz = 0;
    let sx = 0;
    let sy = 0;
    let sd = 0;
    const outer = viewLoops[0];
    outer.forEach((current, i) => {
      const next = outer[(i + 1) % outer.length];
      const [x0, y0] = current.point;
      const [x1, y1] = next.point;
      nx += (y0 - y1) * (current.depth + next.depth);
      ny += (current.depth - next.depth) * (x0 + x1);
      nz += (x0 - x1) * (y0 + y1);
      sx += x0;
      sy += y0;
      sd += current.depth;
    });
    if (Math.abs(nz) < 1e-12) return;
    const count = outer.length;
    const a = -nx / nz;
    const b = -ny / nz;
    const c = sd / count - a * (sx / count) - b * (sy / count);

    const points = viewLoops.map((loop) => loop.map((vertex) => vertex.point));
    const xs = points[0].map(([x]) => x);
    const ys = points[0].map(([, y]) => y);
    const occluder: Occluder = {
      loops: points,
      a,
      b,
      c,
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys),
    };
    this.extent = Math.max(
      this.extent,
      Math.abs(occluder.minX),
      Math.abs(occluder.maxX),
      Math.abs(occluder.minY),
      Math.abs(occluder.maxY),
      ...outer.map(({ depth }) => Math.abs(depth)),
    );
    this.occluders.push(occluder);
  }

  addEdge(a: Point3D, b: Point3D, title?: string): void {
    this.edges.push({ from: this.toView(a), to: this.toView(b), title });
  }

  /** Splits every edge into visible and hidden pieces against all registered occluders. */
  resolve(): ResolvedEdgePiece[] {
    const epsilon = 1e-6 * Math.max(1, this.extent);
    return this.edges.flatMap((edge) => this.split(edge, epsilon));
  }

  private split({ from, to, title }: PendingEdge, epsilon: number): ResolvedEdgePiece[] {
    const [x0, y0] = from.point;
    const [x1, y1] = to.point;
    const minX = Math.min(x0, x1) - epsilon;
    const maxX = Math.max(x0, x1) + epsilon;
    const minY = Math.min(y0, y1) - epsilon;
    const maxY = Math.max(y0, y1) + epsilon;
    const candidates = this.occluders.filter(
      (o) => o.maxX >= minX && o.minX <= maxX && o.maxY >= minY && o.minY <= maxY,
    );

    const breaks = [0, 1];
    for (const occluder of candidates) {
      for (const loop of occluder.loops) {
        for (let i = 0; i < loop.length; i++) {
          const t = crossingParameter(from.point, to.point, loop[i], loop[(i + 1) % loop.length]);
          if (t !== null) breaks.push(t);
        }
      }
      const gap0 = occluder.a * x0 + occluder.b * y0 + occluder.c - from.depth;
      const gap1 = occluder.a * x1 + occluder.b * y1 + occluder.c - to.depth;
      if (gap0 * gap1 < 0) breaks.push(gap0 / (gap0 - gap1));
    }
    breaks.sort((p, q) => p - q);

    const isHidden = (t: number): boolean => {
      const point = lerpPoint(from.point, to.point, t);
      const depth = from.depth + (to.depth - from.depth) * t;
      return candidates.some(
        (o) =>
          point[0] >= o.minX &&
          point[0] <= o.maxX &&
          point[1] >= o.minY &&
          point[1] <= o.maxY &&
          o.a * point[0] + o.b * point[1] + o.c > depth + epsilon &&
          insideLoops(point, o.loops),
      );
    };

    const pieces: { t0: number; t1: number; hidden: boolean }[] = [];
    for (let i = 0; i < breaks.length - 1; i++) {
      const t0 = breaks[i];
      const t1 = breaks[i + 1];
      if (t1 - t0 < 1e-9) continue;
      const hidden = isHidden((t0 + t1) / 2);
      const last = pieces[pieces.length - 1];
      if (last && last.hidden === hidden) last.t1 = t1;
      else pieces.push({ t0, t1, hidden });
    }

    return pieces.map(({ t0, t1, hidden }) => ({
      from: lerpPoint(from.point, to.point, t0),
      to: lerpPoint(from.point, to.point, t1),
      hidden,
      title,
    }));
  }
}
